import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import { formatDate, formatNumber } from "../../utils/format";
import ErrorModal from "../../components/ErrorModal";
import MultiSelectDropdown from "../../components/MultiSelectDropdown";
import DateRangePresetPicker from "../../components/DateRangePresetPicker";
import PaginationBar from "../../components/PaginationBar";
import { usePermission } from "../../hooks/usePermission";
import MedirisImportPanel from "./MedirisImportPanel";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

export default function ResultsConsultations({ user }) {
  const { hasPermission } = usePermission();
  const canImport = hasPermission("results.import");

  const [members, setMembers] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ categories: [], afrekeningen: [], betaalwijzen: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [showImportPanel, setShowImportPanel] = useState(false);

  // Filters
  const [filterMemberId, setFilterMemberId] = useState("");
  const [datePreset, setDatePreset] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterCategorieList, setFilterCategorieList] = useState([]);
  const [filterAfrekeningList, setFilterAfrekeningList] = useState([]);
  const [filterBetaalwijzeList, setFilterBetaalwijzeList] = useState([]);

  useEffect(() => {
    apiFetch("/results/members")
      .then(setMembers)
      .catch(() => {});
    apiFetch("/results/consultations/filter-options")
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  // Beschermt tegen een race condition: als een oudere, tragere request later
  // terugkomt dan een nieuwere (bv. na een snelle wijziging van pageSize/filter),
  // mag enkel het antwoord van de LAATST gestarte request nog de state bijwerken.
  const fetchIdRef = useRef(0);

  useEffect(() => {
    fetchConsultations();
  }, [page, pageSize, filterMemberId, filterDateFrom, filterDateTo, filterCategorieList, filterAfrekeningList, filterBetaalwijzeList]);

  const fetchConsultations = () => {
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    const params = new URLSearchParams({
      limit: pageSize,
      offset: page * pageSize,
    });
    if (filterMemberId) params.append("member_id", filterMemberId);
    if (filterDateFrom) params.append("date_from", filterDateFrom);
    if (filterDateTo) params.append("date_to", filterDateTo);
    filterCategorieList.forEach((c) => params.append("categorie", c));
    filterAfrekeningList.forEach((a) => params.append("afrekening", a));
    filterBetaalwijzeList.forEach((b) => params.append("betaalwijze", b));

    apiFetch(`/results/consultations?${params.toString()}`)
      .then((data) => {
        if (requestId !== fetchIdRef.current) return;
        setItems(data.items);
        setTotal(data.total);
        setSummary(data.summary);
      })
      .catch((err) => {
        if (requestId !== fetchIdRef.current) return;
        setErrorModal({ show: true, message: err.message || "Fout bij ophalen van consultaties." });
      })
      .finally(() => {
        if (requestId === fetchIdRef.current) setLoading(false);
      });
  };

  const handleFilterChange = () => {
    setPage(0);
  };

  const handleDateRangeChange = (from, to) => {
    setFilterDateFrom(from);
    setFilterDateTo(to);
    handleFilterChange();
  };

  const resetFilters = () => {
    setFilterMemberId("");
    setDatePreset("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterCategorieList([]);
    setFilterAfrekeningList([]);
    setFilterBetaalwijzeList([]);
    setPage(0);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(0);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-4 sm:p-6 max-w-full">
      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onClose={() => setErrorModal({ show: false, message: "" })}
        />
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Consultaties</h1>
        {canImport && (
          <button
            type="button"
            onClick={() => setShowImportPanel(true)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Importeer Mediris data
          </button>
        )}
      </div>

      {showImportPanel && (
        <MedirisImportPanel
          onClose={() => setShowImportPanel(false)}
          onImported={fetchConsultations}
        />
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase">Filters</span>
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Filters wissen
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="w-56">
            <label className="block text-xs font-medium text-gray-600 mb-1">Medewerker</label>
            <select
              value={filterMemberId}
              onChange={(e) => {
                setFilterMemberId(e.target.value);
                handleFilterChange();
              }}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Alle</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.naam}</option>
              ))}
            </select>
          </div>

          <div className="w-56">
            <MultiSelectDropdown
              label="Categorie"
              options={filterOptions.categories}
              selected={filterCategorieList}
              onChange={(list) => {
                setFilterCategorieList(list);
                handleFilterChange();
              }}
            />
          </div>

          <div className="w-56">
            <MultiSelectDropdown
              label="Afrekening"
              options={filterOptions.afrekeningen}
              selected={filterAfrekeningList}
              onChange={(list) => {
                setFilterAfrekeningList(list);
                handleFilterChange();
              }}
            />
          </div>

          <div className="w-56">
            <MultiSelectDropdown
              label="Betaalwijze"
              options={filterOptions.betaalwijzen}
              selected={filterBetaalwijzeList}
              onChange={(list) => {
                setFilterBetaalwijzeList(list);
                handleFilterChange();
              }}
            />
          </div>

          <div className="w-56">
            <DateRangePresetPicker
              preset={datePreset}
              onPresetChange={setDatePreset}
              onChange={handleDateRangeChange}
            />
          </div>

          {datePreset === "custom" && (
            <>
              <div className="w-40">
                <label className="block text-xs font-medium text-gray-600 mb-1">Van</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => handleDateRangeChange(e.target.value, filterDateTo)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="w-40">
                <label className="block text-xs font-medium text-gray-600 mb-1">Tot</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => handleDateRangeChange(filterDateFrom, e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="flex justify-between items-center mb-3 text-sm text-gray-500">
        <span>{total} consultaties gevonden</span>
      </div>

      {/* Table - blijft zichtbaar tijdens het laden van een nieuwe pagina/filter; enkel een
          overlay-spinner erbovenop, de huidige rijen verdwijnen pas zodra de nieuwe er zijn. */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Datum</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Lid</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Consultatietype</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Categorie</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">Gevraagd</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Betaalwijze</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Afrekening</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap">Bron</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-10 text-center text-gray-400">
                    Geen consultaties gevonden.
                  </td>
                </tr>
              ) : (
                items.map((c, idx) => (
                  <tr
                    key={c.id}
                    className={idx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50 hover:bg-gray-100"}
                  >
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {formatDate(c.datum)}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {c.member_naam || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs max-w-xs truncate">
                      {c.consultatietype || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {c.categorie_naam ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {c.categorie_naam}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-800 whitespace-nowrap">
                      {c.totaal_gevraagd !== null
                        ? `€ ${formatNumber(c.totaal_gevraagd, 2)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.betaalwijze ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            c.is_cash
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {c.betaalwijze}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap text-xs">
                      {c.afrekening || "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.bron === "historisch" ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800"
                          title="Samengestelde schatting uit de INKOMSTENFILE, geen individueel patiëntbezoek"
                        >
                          Historisch
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                          Mediris
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {summary && total > 0 && (
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold text-gray-700">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {summary.datum_min && summary.datum_max
                      ? formatDate(summary.datum_min) === formatDate(summary.datum_max)
                        ? formatDate(summary.datum_min)
                        : `${formatDate(summary.datum_min)} - ${formatDate(summary.datum_max)}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {summary.behandelaars_count > 1
                      ? `${summary.behandelaars_count} behandelaars`
                      : summary.member_names?.[0] || "—"}
                  </td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {`€ ${formatNumber(summary.totaal_gevraagd, 2)}`}
                  </td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <PaginationBar
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}
